import {type AxiosInstance, type AxiosResponse, type ResponseType} from "axios";
import Vue, { type Ref } from "vue";
import PushNotification from "@/models/User/PushNotification";
import type { ApiPaginator, ApiParams } from "@/modules/api/types.ts";
import objectToFormData from "object-to-formdata";

type ApiPaginatedResponse<T> = {
    data: T[];
    meta: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
    };
};

export class ApiService {
    private _url: string = "";
    private _method: "get" | "post" | "put" | "delete" = "get";
    private _params: ApiParams = {};
    private _data: FormData = new FormData();
    private _responseType?: ResponseType;
    private _axiosInstance: AxiosInstance;

    constructor(axiosInstance: AxiosInstance) {
        this._axiosInstance = axiosInstance;
    }

    url(url: string, params?: ApiParams): this {
        this._url = url;
        if (params) {
            this._params = params;
        }
        return this;
    }

    data(data: unknown): this {
        this._data = objectToFormData.serialize(data, {
            allowEmptyArrays: true,
            booleansAsIntegers: true,
            indices: true,
        });

        return this;
    }

    responseType(responseType: ResponseType): this {
        this._responseType = responseType;
        return this;
    }

    get(): this {
        this._method = "get";

        return this;
    }

    post(): this {
        this._method = "post";

        return this;
    }

    put(): this {
        this._method = "put";

        return this;
    }

    delete(): this {
        this._method = "delete";

        return this;
    }

    single<M>(): Promise<M> {
        return this._request<M>();
    }

    async first<M>(): Promise<M | null> {
        this._params.limit = 1;
        const listResponse = await this._request<M[]>();

        if (listResponse[0] === undefined) {
            return null;
        }

        return listResponse[0];
    }

    list<M>(): Promise<M[]> {
        this._params.paginate = 0;
        return this._request<M[]>();
    }

    async paginated<M>(paginator: Ref<ApiPaginator>): Promise<M[]> {
        this._params.paginate = 1;

        const paginatedResponse =
            await this._request<ApiPaginatedResponse<M>>();

        if (paginatedResponse.meta === undefined) {
            paginator.value = {
                ...paginator.value,
                currentPage: 1,
                total: 0,
                lastPage: 1,
            };
            return [];
        }

        paginator.value = {
            sortBy: paginator.value.sortBy,
            currentPage: paginatedResponse.meta.current_page,
            perPage: paginatedResponse.meta.per_page,
            total: paginatedResponse.meta.total,
            lastPage: paginatedResponse.meta.last_page,
        };

        return paginatedResponse.data;
    }

    private async _request<M>(): Promise<M> {
        if (!this._url) {
            throw new Error(
                "API url is not set. Call url() before performing a request.",
            );
        }

        if (this._method === "put") {
            this._method = "post";
            this._data.append("_method", "put");
        }

        try {
            const response: AxiosResponse<M> =
                await this._axiosInstance.request<M>({
                    method: this._method,
                    url: this._url,
                    params: this._params,
                    data: this._data,
                    responseType: this._responseType,
                });

            this._reset();

            return response.data;
        } catch (error: unknown) {
            // Re-throw with a cleaner message while preserving original error details
            const e = error as {
                response?: { status?: number; data?: { message?: string } };
                message?: string;
            };
            const status = e?.response?.status;
            const serverMessage = e?.response?.data?.message;
            const message = serverMessage || e?.message || "Request failed";
            const wrapped = new Error(
                `[API ${this._method.toUpperCase()} ${this._url}] ${status ? status + " " : ""}${message}`,
            );
            // Attach original error for debugging contexts that inspect it
            (wrapped as Error & { cause?: unknown }).cause = error;
            // alert message for user
            if (!Vue.prototype.$dialog) {
                alert(wrapped.message);
            } else {
                PushNotification.error(wrapped.message);
            }

            this._reset();

            throw wrapped;
        }
    }

    private _reset(): void {
        this._url = "";
        this._method = "get";
        this._params = {};
        this._data = new FormData();
    }
}
